import type {
    GenerationBlockScoreSchema,
    GenerationCriteriaScoreSchema,
    LlmSubblocksTitleTypes,
    ProductCardLlmBlockTypes,
    ValidateProductScoresSchema,
} from '@/common/api/client/types.gen'
import { exportRowsToExcel, type ExcelExportColumn } from './exportExcel'
import { getBlockTitle, getCriteriaLabel } from './labels'
import { formatScoreValue } from './scoreColors'

export type ProductScoresBlockColumn = {
    type: 'block'
    key: string
    header: string
    blockType: ProductCardLlmBlockTypes
}

export type ProductScoresCriteriaColumn = {
    type: 'criteria'
    key: string
    header: string
    blockType: ProductCardLlmBlockTypes
    titleType: LlmSubblocksTitleTypes
    criteriaName: string
}

export type ProductScoresTableColumn = ProductScoresBlockColumn | ProductScoresCriteriaColumn

const buildCriteriaKey = (
    blockType: ProductCardLlmBlockTypes,
    titleType: LlmSubblocksTitleTypes,
    criteriaName: string
) => `${blockType}:${titleType}:${criteriaName}`

const getBlockTableHeader = (block: GenerationBlockScoreSchema): string => {
    const title = getBlockTitle(block.block_type, block.title)
    return block.max_score > 0 ? `${title} /${block.max_score}` : title
}

export const collectTableColumns = (items: ValidateProductScoresSchema[]): ProductScoresTableColumn[] => {
    const referenceProduct = items[0]
    if (!referenceProduct) return []

    const columns: ProductScoresTableColumn[] = []

    for (const block of referenceProduct.blocks) {
        const criterias = block.criterias ?? []
        if (criterias.length === 0) continue

        columns.push({
            type: 'block',
            key: `block:${block.block_type}`,
            header: getBlockTableHeader(block),
            blockType: block.block_type,
        })

        for (const criteria of criterias) {
            columns.push({
                type: 'criteria',
                key: buildCriteriaKey(block.block_type, criteria.title_type, criteria.criteria_name),
                header: getCriteriaLabel(criteria.title, criteria.title_type, criteria.criteria_name),
                blockType: block.block_type,
                titleType: criteria.title_type,
                criteriaName: criteria.criteria_name,
            })
        }
    }

    return columns
}

export const findBlock = (
    product: ValidateProductScoresSchema,
    blockType: ProductCardLlmBlockTypes
): GenerationBlockScoreSchema | undefined => product.blocks.find(b => b.block_type === blockType)

export const findCriteria = (
    product: ValidateProductScoresSchema,
    column: ProductScoresCriteriaColumn
): GenerationCriteriaScoreSchema | undefined => {
    const block = findBlock(product, column.blockType)
    return block?.criterias?.find(c => c.title_type === column.titleType && c.criteria_name === column.criteriaName)
}

export const formatBlockPercent = (block: GenerationBlockScoreSchema | undefined): number | null => {
    if (!block) return null

    if (block.percent != null) {
        return Math.min(100, Math.max(0, block.percent))
    }

    const maxScore = block.max_score > 0 ? block.max_score : 0
    if (maxScore <= 0) return null

    return Math.min(100, Math.max(0, (block.score / maxScore) * 100))
}

export const isCriteriaPassed = (criteria: GenerationCriteriaScoreSchema | undefined): boolean =>
    criteria?.conformity_score === 1

const getCellExportValue = (product: ValidateProductScoresSchema, col: ProductScoresTableColumn): string => {
    if (col.type === 'block') {
        const block = findBlock(product, col.blockType)
        const percent = formatBlockPercent(block)
        return percent != null ? `${Math.round(percent)}%` : '—'
    }

    const criteria = findCriteria(product, col)
    return isCriteriaPassed(criteria) ? '✓' : '—'
}

export const exportProductsScoresToExcel = (products: ValidateProductScoresSchema[], filename: string): void => {
    const tableColumns = collectTableColumns(products)
    const columns: ExcelExportColumn<ValidateProductScoresSchema>[] = [
        { header: '#', getValue: (_row, index) => index + 1 },
        { header: 'Название', getValue: row => row.name },
        {
            header: 'Итог',
            getValue: row => `${formatScoreValue(row.score)} / ${formatScoreValue(row.max_score)}`,
        },
        ...tableColumns.map(
            (col): ExcelExportColumn<ValidateProductScoresSchema> => ({
                header: col.header,
                getValue: row => getCellExportValue(row, col),
            })
        ),
    ]

    exportRowsToExcel(products, columns, filename, 'Товары')
}
